/**
 * Pure helpers for distinguishing video classes from live classes on the
 * web admin. Mirrors the RN file at
 * happily_reactnative_new/src/components/university/videoClassHelpers.js
 * (web subset: constants + URL parser + role check).
 *
 * Source of truth for the `class_type` discriminator field on
 * `classrooms` Firestore docs.
 */

export const CLASS_TYPE = Object.freeze({
  LIVE: 'live',
  VIDEO: 'video',
});

/**
 * Source of a video class. 'upload' = native MP4 in Firebase Storage.
 * 'youtube' = an external YouTube video, played via the YouTube IFrame
 * Player API in the RN app.
 */
export const VIDEO_SOURCE = Object.freeze({
  UPLOAD: 'upload',
  YOUTUBE: 'youtube',
});

/**
 * Default XP awarded for completing a video class. Kept as a literal
 * here on the web admin (the read-side authoritative value lives in the
 * RN xpRules catalog).
 */
export const VIDEO_XP_REWARD = 25;

/**
 * Preset XP amounts a privileged creator can choose from when publishing
 * a video. Kept short so the picker stays a single row of chips.
 */
export const VIDEO_XP_PRESETS = Object.freeze([10, 25, 50, 100]);

/**
 * Roles allowed to set a custom XP amount on a video class.
 * Mirrors the privilege check used in the RN app and elsewhere in this
 * web admin.
 */
export const PRIVILEGED_ROLES = Object.freeze([
  'admin',
  'hr',
  'superadmin',
  'leadership',
  'superleader',
]);

/**
 * Parse a YouTube URL or bare video ID and return the 11-character video ID.
 * Accepted forms:
 *   - https://www.youtube.com/watch?v=ID[&...]
 *   - https://youtube.com/watch?v=ID
 *   - https://m.youtube.com/watch?v=ID
 *   - https://youtu.be/ID[?...]
 *   - https://www.youtube.com/embed/ID[?...]
 *   - https://www.youtube.com/shorts/ID
 *   - bare 11-char ID (e.g. "dQw4w9WgXcQ")
 * Returns null if no valid video ID is found.
 */
const YT_BARE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const YT_URL_PATTERNS = [
  /(?:youtube\.com\/watch\?(?:[^&]*&)*v=)([A-Za-z0-9_-]{11})/,
  /(?:youtu\.be\/)([A-Za-z0-9_-]{11})/,
  /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
  /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
];

export function parseYouTubeVideoId(input) {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (YT_BARE_ID_PATTERN.test(trimmed)) return trimmed;
  for (const re of YT_URL_PATTERNS) {
    const m = trimmed.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
}

export function isValidYouTubeUrl(input) {
  return parseYouTubeVideoId(input) !== null;
}

/**
 * Returns true when the given user is allowed to override the default
 * video XP amount. Accepts either the user object (RN parity, reads
 * `user_type`) or a bare role string for convenience.
 */
export function canSetVideoXP(userOrType) {
  if (!userOrType) return false;
  const role = typeof userOrType === 'string'
    ? userOrType
    : userOrType.user_type || userOrType.type;
  return PRIVILEGED_ROLES.includes(role);
}

/**
 * Capture a single frame from an MP4 (or any browser-decodable video file)
 * and return it as a Blob suitable for upload. Used to populate `uri_image`
 * on the media[] entry — RN's VideoCard falls back to a generic thumb when
 * uri_image is empty, so failure here is non-fatal.
 *
 * Strategy: load the file into a hidden <video>, seek a short distance in
 * (1s or 1/4 of duration, whichever is smaller — avoids any black-frame
 * intro), draw to <canvas>, export as JPEG.
 */
export function captureVideoThumbnail(file, opts = {}) {
  return new Promise((resolve, reject) => {
    if (!file) { reject(new Error('No file')); return; }
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = url;

    let settled = false;
    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute('src');
      try { video.load(); } catch (_) { /* ignore */ }
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    };
    const ok = (blob) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    };

    video.addEventListener('loadedmetadata', () => {
      const seekTo = Math.min(1, (video.duration || 4) / 4);
      try { video.currentTime = seekTo; } catch (e) { fail(e); }
    });

    video.addEventListener('seeked', () => {
      try {
        const maxW = opts.maxWidth || 640;
        const ratio = (video.videoWidth || 16) / (video.videoHeight || 9);
        const w = Math.min(maxW, video.videoWidth || maxW);
        const h = Math.round(w / ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => (blob ? ok(blob) : fail(new Error('toBlob returned null'))),
          opts.mimeType || 'image/jpeg',
          opts.quality != null ? opts.quality : 0.85
        );
      } catch (e) { fail(e); }
    });

    video.addEventListener('error', () => fail(new Error('Video failed to load')));
    setTimeout(() => fail(new Error('Thumbnail capture timed out')), 15000);
  });
}
